(() => {
  const MOBILE_BREAKPOINT = 999;
  const EDGE = -0.05;
  const MOTION_PATH_URL =
    window.themeAssets && window.themeAssets.motionPathPlugin;

  let motionPathLoading = null;

  function isMobile() {
    return window.innerWidth <= MOBILE_BREAKPOINT;
  }

  function shouldReduceMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  function hasArcCompare() {
    return document.querySelector(".arc-compare");
  }

  function waitForCoreLibs(callback, attempts = 0) {
    if (window.gsap && window.ScrollTrigger) {
      callback();
      return;
    }

    if (attempts > 100) {
      console.warn("GSAP or ScrollTrigger did not load.");
      return;
    }

    setTimeout(() => {
      waitForCoreLibs(callback, attempts + 1);
    }, 50);
  }

  function loadMotionPathPlugin() {
    if (window.MotionPathPlugin) {
      return Promise.resolve();
    }

    if (!MOTION_PATH_URL) {
      console.warn("MotionPathPlugin URL is missing. Define window.themeAssets.motionPathPlugin in theme.liquid.");
      return Promise.reject();
    }

    if (motionPathLoading) {
      return motionPathLoading;
    }

    motionPathLoading = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = MOTION_PATH_URL;
      script.async = true;
      script.onload = resolve;
      script.onerror = reject;
      document.body.appendChild(script);
    });

    return motionPathLoading;
  }

  function initArcCompare() {
    const sections = document.querySelectorAll(".arc-compare");
    if (!sections.length) return;

    sections.forEach((root) => {
      if (isMobile() || shouldReduceMotion()) {
        if (root.__arcCompareCleanup) {
          root.__arcCompareCleanup();
        }

        root.classList.add("is-mobile");
      }
    });

    if (isMobile() || shouldReduceMotion()) return;

    waitForCoreLibs(() => {
      loadMotionPathPlugin()
        .then(() => {
          if (!window.MotionPathPlugin) return;

          gsap.registerPlugin(MotionPathPlugin, ScrollTrigger);

          sections.forEach((root) => {
            if (root.__arcCompareCleanup) {
              root.__arcCompareCleanup();
            }

            root.classList.remove("is-mobile");

            const pathTop = root.querySelector("[data-path-top]");
            const pathBottom = root.querySelector("[data-path-bottom]");

            if (!pathTop || !pathBottom) return;

            const allA = Array.from(
              root.querySelectorAll('.arc-compare__item[data-side="a"]')
            );
            const allB = Array.from(
              root.querySelectorAll('.arc-compare__item[data-side="b"]')
            );

            const maxA = Math.min(5, parseInt(root.dataset.maxA || "5", 10));
            const maxB = Math.min(5, parseInt(root.dataset.maxB || "5", 10));

            const listA = allA.slice(0, maxA);
            const listB = allB.slice(0, maxB);
            const visibleItems = [...listA, ...listB];

            allA.forEach((el, index) => {
              el.style.display = index < listA.length ? "" : "none";
            });

            allB.forEach((el, index) => {
              el.style.display = index < listB.length ? "" : "none";
            });

            const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

            const topLen = pathTop.getTotalLength();
            const bottomLen = pathBottom.getTotalLength();

            const getAngleAt = (path, len, t) => {
              const L = len * t;
              const p = path.getPointAtLength(L);
              const p2 = path.getPointAtLength(Math.min(len, L + 2));
              const angle =
                (Math.atan2(p2.y - p.y, p2.x - p.x) * 180) / Math.PI;

              return clamp(angle, -18, 18);
            };

            const spread = 1 - EDGE * 2;
            const getT = (i, total) =>
              EDGE + ((i + 1) / (total + 1)) * spread;

            gsap.set(visibleItems, {
              opacity: 0,
              willChange: "transform, opacity",
            });

            listA.forEach((el) => {
              gsap.set(el, {
                motionPath: {
                  path: pathTop,
                  align: pathTop,
                  alignOrigin: [0, 0],
                  start: 0.02,
                  end: 0.02,
                },
              });
            });

            listB.forEach((el) => {
              gsap.set(el, {
                motionPath: {
                  path: pathBottom,
                  align: pathBottom,
                  alignOrigin: [0, 0],
                  start: 0.98,
                  end: 0.98,
                },
              });
            });

            const tlA = gsap.timeline({
              paused: true,
              defaults: { ease: "none" },
            });

            const tlB = gsap.timeline({
              paused: true,
              defaults: { ease: "none" },
            });

            listA.forEach((el, i) => {
              const tEnd = getT(i, listA.length);

              el.style.setProperty(
                "--rot",
                getAngleAt(pathTop, topLen, tEnd) + "deg"
              );

              tlA.to(
                el,
                {
                  opacity: 1,
                  duration: 1,
                  motionPath: {
                    path: pathTop,
                    align: pathTop,
                    alignOrigin: [0, 0],
                    start: 0.02,
                    end: tEnd,
                  },
                },
                i * 0.12
              );
            });

            listB.forEach((el, i) => {
              const tEnd = getT(i, listB.length);
              const mirroredT = 1 - tEnd;
              const delayIndex = listB.length - 1 - i;

              el.style.setProperty(
                "--rot",
                getAngleAt(pathBottom, bottomLen, mirroredT) + "deg"
              );

              tlB.to(
                el,
                {
                  opacity: 1,
                  duration: 1,
                  motionPath: {
                    path: pathBottom,
                    align: pathBottom,
                    alignOrigin: [0, 0],
                    start: 0.98,
                    end: mirroredT,
                  },
                },
                delayIndex * 0.12
              );
            });

            const triggerA = ScrollTrigger.create({
              animation: tlA,
              trigger: root,
              start: "top 75%",
              end: () => "+=" + Math.round(window.innerHeight * 0.55),
              scrub: 0.7,
              invalidateOnRefresh: false,
            });

            const triggerB = ScrollTrigger.create({
              animation: tlB,
              trigger: root,
              start: "top 5%",
              end: () => "+=" + Math.round(window.innerHeight * 0.4),
              scrub: 0.7,
              invalidateOnRefresh: false,
            });

            root.__arcCompareCleanup = () => {
              triggerA.kill();
              triggerB.kill();
              tlA.kill();
              tlB.kill();

              gsap.killTweensOf(visibleItems);

              gsap.set(visibleItems, {
                clearProps: "willChange",
              });

              root.__arcCompareCleanup = null;
            };
          });

          requestAnimationFrame(() => {
            ScrollTrigger.refresh();
          });
        })
        .catch(() => {
          console.warn("MotionPathPlugin failed to load.");
        });
    });
  }

  function boot() {
    if (!hasArcCompare()) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(initArcCompare);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }

  window.addEventListener("load", boot, { once: true });

  document.addEventListener("shopify:section:load", boot);
  document.addEventListener("shopify:section:reorder", boot);
  document.addEventListener("shopify:block:select", boot);
})();